import api from './api';

// API endpoints for grape management
const API_ENDPOINTS = {
  GRAPE: '/admin/grape',
};

// Define the GrapeAlias interface
export interface GrapeAlias {
  name: string;
  languageCode?: string;
  isOfficial: boolean;
  notes?: string;
}

// Define the Grape interface
export interface Grape {
  _id: string;
  primaryName: string;
  aliases: GrapeAlias[];
  color: 'RED' | 'WHITE' | 'PINK_ROSE';
  species?: string;
  createdAt: string;
  updatedAt: string;
}

// Define pagination metadata interface
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Define paginated response interface
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

const grapeService = {
  /**
   * Get all grapes
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @param sort Field to sort by
   * @param order Sort order ('asc' or 'desc')
   * @returns Paginated response with all grapes
   */
  getAllGrapes: async (page = 1, pageSize = 20, sort = 'primaryName', order = 'asc'): Promise<PaginatedResponse<Grape>> => {
    const response = await api.get(API_ENDPOINTS.GRAPE, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get a specific grape by ID
   */
  getGrape: async (id: string): Promise<Grape> => {
    const response = await api.get(`${API_ENDPOINTS.GRAPE}/${id}`);
    return response.data;
  },

  /**
   * Create a new grape
   */
  createGrape: async (grape: Omit<Grape, '_id' | 'createdAt' | 'updatedAt'>): Promise<Grape> => {
    const response = await api.post(API_ENDPOINTS.GRAPE, grape);
    return response.data;
  },

  /**
   * Update an existing grape
   */
  updateGrape: async (id: string, grape: Partial<Omit<Grape, '_id' | 'createdAt' | 'updatedAt'>>): Promise<Grape> => {
    const response = await api.put(`${API_ENDPOINTS.GRAPE}/${id}`, grape);
    return response.data;
  },

  /**
   * Delete a grape
   */
  deleteGrape: async (id: string): Promise<void> => {
    await api.delete(`${API_ENDPOINTS.GRAPE}/${id}`);
  }
};

export default grapeService;
